import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, map, Observable, of, timeout} from 'rxjs';
import {environment} from '../../../environments/environment';
import {KiGrafikAnfrage} from '../../model/ki-grafik.model';

/**
 * Der Weg zum KI-Springboot-Service für "KI-Texte erzeugen".
 *
 * Geht, wie der ältere Videotext-Weg ("Daten übernehmen",
 * {@link DmxDocumentService#getKiTextForHessenwetter}), über den
 * wetter-terminal-rest-service (`POST chat/` unter `terminalApiUrl`, hier
 * `ChatModelController.getTerminalChatModelResponse`). Der KI-Springboot-Service selbst
 * ist zur Laufzeit **nicht direkt erreichbar** – in der Cloud liegt er nur unter einer
 * cluster-internen DNS-Adresse (`terminal-ai-module.terminal.svc.cluster.local`), die ein
 * Browser außerhalb des Clusters nicht auflösen kann. Der Rest-Service reicht die
 * Anfrage eins zu eins als Prompt weiter, ohne sie umzuformen.
 *
 * Die RFA steht dabei **nicht** in der URL, sondern im JSON selbst (`anfrage.rfas`) –
 * das Chat-Modell kennt anhand des System-Prompts, wie es die Anfrage auswerten muss.
 *
 * ⚠️ Der Rumpf ist **Text**, nicht JSON: das Modell erwartet die Marken `MAX_CHARS:`,
 * `BOX:` und `JSON:` – siehe {@link baueEingabe}.
 */
/**
 * Stand einer laufenden Anfrage, wie ihn das AI-Modul meldet (`GET chat/status/{anfrageId}`,
 * durchgereicht vom Rest-Service; Spiegel von `KiFortschritt` dort).
 */
export interface KiStatus {
  /** `ERZEUGEN`, `KORRIGIEREN`, `FERTIG` oder `FEHLER`. */
  phase: string;
  /** Nur bei `KORRIGIEREN`: `KUERZEN`, `VERLAENGERN` oder `UEBERARBEITEN`. */
  aufgabe: string | null;
  /** Wie lange die Phase schon läuft, in Millisekunden. */
  seitMs: number;
}

@Injectable({providedIn: 'root'})
export class KiChatService {

  /**
   * Wie lange auf den KI-Text gewartet wird. Eine Anfrage sind mehrere Modellaufrufe
   * nacheinander (Text, dann Kürzung/Verlängerung samt Wiederholungen), 180 s reichten
   * mehrfach nicht. Bewusst etwas **länger** als der Rest-Service
   * (`ChatModelController.KI_TIMEOUT_MS`, 300 s) – sonst gibt der Browser auf, während der
   * Server noch auf die Antwort wartet.
   */
  private static readonly TIMEOUT_MS = 360000;

  /**
   * Zeichenzahl, die gilt, wenn die Ziel-Textbox keine nennt.
   *
   * Es gibt keinen sinnvollen "unbegrenzt"-Wert: der Prompt muss `MAX_CHARS` **immer**
   * enthalten, sonst versteht das Modell die Anfrage nicht. Der Wert entspricht der
   * größten Grenze im Template (Heute/Nacht/Morgen) und wird zusammen mit einer Warnung
   * benutzt – fehlt die Grenze, ist etwas an der Zuordnung faul.
   */
  private static readonly MAX_CHARS_ERSATZ = 500;

  private readonly url: string = `${environment.terminalApiUrl}/chat/`;
  /**
   * `text/plain`, weil der Rumpf **Text** ist und kein JSON (siehe {@link baueEingabe}).
   * Nebeneffekt, den man kennen sollte: damit bleibt die Anfrage ein "simple request",
   * der Browser schickt also keinen OPTIONS-Preflight.
   */
  private readonly httpHeader = new HttpHeaders({'Content-Type': 'text/plain'});

  constructor(private httpClient: HttpClient) {
  }

  /**
   * Schickt die Grafik-Werte an das Chat-Modell.
   *
   * @param maxChars Zeichengrenze der Textbox, in die die Antwort gehört. Kommt aus dem
   *   Server-Template (`TextArea.maxChars`) und wird nicht geraten.
   * @param box Name dieser Textbox ("Schlagzeile", "Heute", …, "1.Aussichtentag", siehe
   *   `KiGrafikService.boxNameFuer`). Das AI-Modul lädt darüber die Regeln der Box.
   *
   * Die Antwort wird als Text gelesen, nicht als JSON: der Service liefert den
   * erzeugten Wettertext, und ein `responseType: 'json'` würde an einer nackten
   * Textantwort scheitern.
   */
  public erzeugeTexte(anfrage: KiGrafikAnfrage, maxChars?: number | null,
                      box?: string | null, anfrageId?: string | null): Observable<string> {
    // Die Id der Fortschrittsanzeige geht als Query-Parameter mit, nicht als Header: ein
    // eigener Header machte die Anfrage zum Preflight-Fall (siehe httpHeader).
    const params = anfrageId ? new HttpParams().set('anfrageId', anfrageId) : undefined;
    return this.httpClient.post(this.url, KiChatService.baueEingabe(anfrage, maxChars, box),
      {headers: this.httpHeader, responseType: 'text', params})
      .pipe(timeout(KiChatService.TIMEOUT_MS));
  }

  /**
   * Stand einer laufenden Anfrage für die Fortschrittsanzeige – oder `null`, wenn es keinen
   * gibt: noch nicht angekommen, unbekannt (404), AI-Modul nicht erreichbar (204) oder ein
   * älterer Rest-Service ohne diesen Endpunkt. Wirft nie: ein fehlender Stand ist nur eine
   * schlichtere Anzeige, kein Fehler der Anfrage.
   */
  public status(anfrageId: string): Observable<KiStatus | null> {
    return this.httpClient.get<KiStatus | null>(
      `${environment.terminalApiUrl}/chat/status/${encodeURIComponent(anfrageId)}`)
      .pipe(
        map(status => status?.phase ? status : null),
        catchError(() => of(null))
      );
  }

  /**
   * Klartext, woran eine KI-Anfrage gescheitert ist – für die rote Hinweisleiste und die
   * rote Lampe neben dem Knopf.
   *
   * Drei Fälle, die vorher alle "keine Antwort" hießen:
   * - `TimeoutError`: der Client selbst hat nach `TIMEOUT_MS` aufgegeben.
   * - HTTP-Status: Rest-Service oder AI-Modul haben mit einem Fehler geantwortet.
   * - Status 0: die Verbindung wurde unterwegs abgebrochen, ohne lesbare Antwort. Typisch
   *   für ein Gateway oder einen Load Balancer mit kürzerem Timeout als die Kette dahinter –
   *   seine 504 kommt ohne CORS-Header, der Browser zeigt deshalb nur Status 0.
   */
  public static fehlerUrsache(fehler: {name?: string; status?: number} | null | undefined): string {
    if (fehler?.name === 'TimeoutError') {
      return 'Zeitüberschreitung im Browser';
    }
    if (fehler?.status) {
      return `HTTP ${fehler.status}`;
    }
    return 'Verbindung vor der Antwort abgebrochen (Gateway-Timeout oder Netzwerk)';
  }

  /**
   * Der Rumpf der Anfrage: **Text mit eingebettetem JSON**, in genau dieser Form.
   *
   * ```
   * MAX_CHARS: 500
   * BOX: Heute
   * JSON:
   * {
   *   "rfas": [ ... ]
   * }
   * ```
   *
   * ⚠️ Beide Marken sind Pflicht – so erwartet es das Modell hinter `chat/`. Ein reines
   * JSON, wie es dieser Weg zunächst geschickt hat, versteht es nicht mehr. `MAX_CHARS`
   * ist die Zeichengrenze der Ziel-Textbox; ohne sie schreibt das Modell an der Box
   * vorbei, denn der Endpunkt `chat/` reicht den Rumpf eins zu eins durch und ergänzt –
   * anders als `chat/hrtext` – **nichts**.
   *
   * `BOX` nennt die Ziel-Textbox; das AI-Modul lädt darüber deren Regeln (etwa "Steno
   * ohne Verben" für die Schlagzeile) aus seiner Datenbank. Ist keine bekannt, fehlt die
   * Zeile – mit Warnung, denn dann fehlen dem Modell diese Regeln.
   *
   * Eingerückt wird das JSON, weil es so auch im Netzwerk-Protokoll und im Log lesbar
   * bleibt; für das Modell ist die Einrückung gleichgültig.
   */
  private static baueEingabe(anfrage: KiGrafikAnfrage, maxChars?: number | null,
                             box?: string | null): string {
    let grenze = maxChars;
    if (!grenze || grenze <= 0) {
      console.warn('KI-Anfrage ohne Zeichengrenze der Ziel-Textbox, benutze '
        + KiChatService.MAX_CHARS_ERSATZ);
      grenze = KiChatService.MAX_CHARS_ERSATZ;
    }
    let boxZeile = '';
    if (box) {
      boxZeile = `BOX: ${box}\n`;
    } else {
      console.warn('KI-Anfrage ohne Namen der Ziel-Textbox, BOX fehlt im Prompt');
    }
    return `MAX_CHARS: ${grenze}\n${boxZeile}JSON:\n${JSON.stringify(anfrage, null, 2)}`;
  }
}
